import type { OrderOptionDto, SupplierOrderLinkHintDto } from '../dto/order';
import type {
  OrderProductRealizationDto,
  OrderTransportsDto,
  RouteOrderDto,
  TransportRouteDetailDto,
} from '../dto/transport';
import {
  buildTransportFormValues,
  buildTransportRequestPayload,
  type TransportFormValues,
} from './transportFormUtils';
import {
  type AllocationMethod as TransportCostAllocationMethod,
  MAX_MONEY_AMOUNT,
  TRAILER_CAPACITY_LDM,
  TRAILER_PAYLOAD_KG,
  allocateProportionally,
  resolveItemLdm,
  roundMoney,
} from '../utils/transportCostAllocation';
import {
  calculateAreaM2,
  calculateVolumeM3,
  convertAllocationQuantity,
  normalizeQuantityUnit,
  type ConversionSource,
} from '../utils/dimensionConversion';
import { formatUnit } from '../utils/units';

// Single home for the form's default transport-cost currency. The create-mode
// default below and every in-form display fallback (preview column, manual
// InputNumber addons, sticky summary) must read this constant rather than
// hardcoding 'USD' or 'PLN' independently, so a future default change is a
// one-line edit. NOT used for the PLN-canonical amounts stored in
// TransportOrderAllocation (see TransportRouteDetail.tsx) — those are always
// literal 'PLN' regardless of this constant.
export const DEFAULT_TRANSPORT_CURRENCY = 'PLN';

/**
 * Map of `orderProductId -> OrderProductRealizationDto` aggregated across all
 * selected orders. Lets the cargo table show "already on X other transports:
 * 120 m² (remaining: 80 m²)" so the user can split one order across multiple
 * trucks without double-allocating.
 */
export type AllocationHintMap = Record<string, OrderProductRealizationDto>;

/**
 * Merge the per-order realization responses into a single hint map keyed by
 * orderProductId. When the form is editing an existing transport, the cargo
 * currently on THIS transport is subtracted from the map so the hint reflects
 * OTHER transports only — otherwise the user would see their own load twice.
 *
 * Kept here (not inline in SharedTransportForm) so the "subtract own cargo"
 * invariant lives in one place and can be unit-tested independently.
 */
export function computeAllocationHints(
  perOrderRealizations: OrderTransportsDto[],
  currentTransport?: Pick<TransportRouteDetailDto, 'cargoItems' | 'routeOrders'> | null,
): AllocationHintMap {
  const ownCargoByProductId: Record<string, number> = {};
  const ownCargoItems = currentTransport?.cargoItems || [];
  const orderIdsWithOwnCargo = new Set<string>();

  ownCargoItems.forEach((item) => {
    if (item.orderId) {
      orderIdsWithOwnCargo.add(item.orderId);
    }

    if (item.orderProductId) {
      ownCargoByProductId[item.orderProductId] =
        (ownCargoByProductId[item.orderProductId] || 0) + Number(item.quantity || 0);
    }
  });

  (currentTransport?.routeOrders || []).forEach((routeOrder: RouteOrderDto) => {
    if (!routeOrder.orderId || orderIdsWithOwnCargo.has(routeOrder.orderId)) {
      return;
    }

    (routeOrder.order?.products || []).forEach((product) => {
      if (!product.id) {
        return;
      }

      ownCargoByProductId[product.id] = Math.max(
        ownCargoByProductId[product.id] || 0,
        Number(product.quantity || 0),
      );
    });
  });

  const merged: AllocationHintMap = {};
  perOrderRealizations.forEach((result) => {
    result.productRealization.forEach((realization) => {
      const ownPortion = ownCargoByProductId[realization.orderProductId] || 0;
      const adjustedAllocated = Math.max(realization.allocatedQuantity - ownPortion, 0);
      merged[realization.orderProductId] = {
        ...realization,
        allocatedQuantity: adjustedAllocated,
        remainingQuantity: Math.max(realization.orderedQuantity - adjustedAllocated, 0),
      };
    });
  });
  return merged;
}

// Distinct, order-preserving list of the delivery/unload places carried by the
// selected orders. Used to prefill the delivery-address blocks in create mode.
export function deriveDeliveryAddressesFromOrders(orders: OrderOptionDto[]): string[] {
  const seen = new Set<string>();
  const addresses: string[] = [];

  orders.forEach((order) => {
    const place = String(order.deliveryLocation || '').trim();
    if (place && !seen.has(place)) {
      seen.add(place);
      addresses.push(place);
    }
  });

  return addresses;
}

// Distinct, order-preserving list of the factory/loading places carried by the
// selected orders. Used to prefill the loading-address blocks in create mode.
export function deriveLoadingAddressesFromOrders(orders: OrderOptionDto[]): string[] {
  const seen = new Set<string>();
  const addresses: string[] = [];

  orders.forEach((order) => {
    const place = String(order.factoryLocation || '').trim();
    if (place && !seen.has(place)) {
      seen.add(place);
      addresses.push(place);
    }
  });

  return addresses;
}

// Deduped, comma-joined customer names of the selected orders. Auto-fills the
// carrier-section customer field so it is never a dead manual field.
export function deriveCustomerFromOrders(orders: OrderOptionDto[]): string {
  const seen = new Set<string>();
  const names: string[] = [];

  orders.forEach((order) => {
    const name = String(order.customerName || '').trim();
    if (name && !seen.has(name)) {
      seen.add(name);
      names.push(name);
    }
  });

  return names.join(', ');
}

// ---------------------------------------------------------------------------
// Supplier purchase-value ("Wartość towaru") prefill.
//
// Rulebook (docs/decisions/2026-07-03-transport-cost-allocation-rules.md,
// point 5): a cargo line's goods value is the PURCHASE value of the position
// excluding transport. When the backend supplies a supplier-order purchase-cost
// hint for a line, the form either uses unitPurchaseCostPLN × loaded quantity
// (explicit item link) or proportionally scales a whole-order purchase value
// (purchaseValuePLN × loaded / purchaseQuantity). It stays operator-editable.
//
// CURRENCY RULE: hints are ALWAYS PLN-canonical. If the transport form currency
// is not PLN we do NOT silently convert — prefill is skipped and the UI shows a
// "przelicz ręcznie" tooltip. Converting silently would fabricate a value the
// operator never saw and never approved, which the rulebook forbids.
// ---------------------------------------------------------------------------

// Currency the supplier hints are denominated in. Prefill only applies when the
// live transport currency matches this exactly.
export const SUPPLIER_HINT_CURRENCY = 'PLN';

/** Map of `orderProductId -> hint` flattened across all selected orders. */
export type SupplierHintMap = Record<string, SupplierOrderLinkHintDto>;

export type SupplierPurchaseBasis =
  | {
      kind: 'wholeOrder';
      purchaseValuePLN: number;
      purchaseQuantity: number;
      purchaseUnit: string | null;
    }
  | { kind: 'unitCost'; unitPurchaseCostPLN: number }
  | { kind: 'none' };

export type CargoHintProvenanceState =
  // A concrete PLN hint whose value can be prefilled/shown as provenance.
  | { kind: 'value'; hint: SupplierOrderLinkHintDto; basis: Exclude<SupplierPurchaseBasis, { kind: 'none' }> }
  // Several supplier sources with conflicting unit costs — no prefill.
  | { kind: 'ambiguous' }
  // A concrete hint exists but the form currency is not PLN — no silent convert.
  | { kind: 'currencyMismatch'; hint: SupplierOrderLinkHintDto; basis: Exclude<SupplierPurchaseBasis, { kind: 'none' }> }
  // No hint for this line.
  | { kind: 'none' };

export interface SupplierPurchaseDisplayAmounts {
  // Saved PLN amount used by the transport-cost calculation.
  plnAmount: number;
  // Exact original-currency amount scaled for the currently loaded quantity.
  // Null means the backend could not identify one honest source amount (for
  // example a mixed-currency whole supplier order).
  sourceAmount: number | null;
  sourceCurrency: string | null;
  // Loaded quantity after conversion into the supplier purchase basis unit.
  purchaseBasisQuantity: number;
}

/**
 * Flatten the per-order `supplierOrderLinks` arrays into one map keyed by
 * orderProductId. Later entries win on key collision (orderProductId is unique
 * per order, so collisions across the flattened set are not expected). Orders
 * without a `supplierOrderLinks` array contribute nothing — that is the
 * "no supplier-price visibility" graceful-absence path.
 */
export function buildSupplierHintMap(orders: OrderOptionDto[]): SupplierHintMap {
  const map: SupplierHintMap = {};
  orders.forEach((order) => {
    (order.supplierOrderLinks || []).forEach((link) => {
      if (link && link.orderProductId) {
        map[link.orderProductId] = link;
      }
    });
  });
  return map;
}

// Single home for deciding which purchase-value basis a hint represents.
// Whole-order basis wins when both legacy/new fields happen to be present.
export function resolveSupplierPurchaseBasis(
  hint: SupplierOrderLinkHintDto | undefined,
): SupplierPurchaseBasis {
  if (!hint || hint.ambiguous) {
    return { kind: 'none' };
  }
  const purchaseValue = toFiniteNumber(hint.purchaseValuePLN);
  const purchaseQuantity = toFiniteNumber(hint.purchaseQuantity);
  if (purchaseValue !== null && purchaseValue > 0 && purchaseQuantity !== null && purchaseQuantity > 0) {
    return {
      kind: 'wholeOrder',
      purchaseValuePLN: purchaseValue,
      purchaseQuantity,
      purchaseUnit: hint.purchaseUnit || null,
    };
  }
  const cost = toFiniteNumber(hint.unitPurchaseCostPLN);
  return cost !== null && cost > 0
    ? { kind: 'unitCost', unitPurchaseCostPLN: cost }
    : { kind: 'none' };
}

/**
 * Classify the provenance state for one cargo line, given the resolved hint (or
 * undefined) and the current form currency. Pure — drives both the prefill
 * decision and the icon/tooltip UI so they can never disagree.
 */
export function resolveCargoHintProvenance(
  hint: SupplierOrderLinkHintDto | undefined,
  formCurrency: string | null | undefined,
): CargoHintProvenanceState {
  if (!hint) {
    return { kind: 'none' };
  }
  if (hint.ambiguous) {
    return { kind: 'ambiguous' };
  }
  const basis = resolveSupplierPurchaseBasis(hint);
  if (basis.kind === 'none') {
    return { kind: 'none' };
  }
  const currency = String(formCurrency || '').trim().toUpperCase();
  if (currency !== SUPPLIER_HINT_CURRENCY) {
    return { kind: 'currencyMismatch', hint, basis };
  }
  return { kind: 'value', hint, basis };
}

function resolvePurchaseBasisQuantity(
  hint: SupplierOrderLinkHintDto,
  basis: Exclude<SupplierPurchaseBasis, { kind: 'none' }>,
  quantity: number | null | undefined,
  quantitySource?: ConversionSource,
): number | null {
  const qty = toFiniteNumber(quantity);
  if (qty === null || qty <= 0) {
    return null;
  }

  const basisUnit = basis.kind === 'wholeOrder'
    ? basis.purchaseUnit
    : (hint.supplierOrderItemUnit || hint.allocatedUnit);
  if (!quantitySource?.unit || !basisUnit) {
    return qty;
  }

  const sourceUnit = normalizeQuantityUnit(quantitySource.unit);
  const targetUnit = normalizeQuantityUnit(basisUnit);
  if (sourceUnit === targetUnit) {
    return qty;
  }

  const conversion = convertAllocationQuantity({
    quantity: qty,
    sourceUnit,
    targetUnit,
    width: quantitySource.width,
    length: quantitySource.length,
    height: quantitySource.height,
    thickness: quantitySource.thickness,
  });
  return conversion.error || conversion.exactValue === null
    ? null
    : conversion.exactValue;
}

/**
 * Resolve both saved PLN and original-currency purchase values for the loaded
 * quantity. Both amounts share the exact same unit-conversion path so a source
 * amount can never drift from the PLN amount for m²/m³/piece conversions.
 * PLN is read from the saved `*PLN` fields rather than recalculated from the
 * rate, preserving the supplier-order rounding snapshot.
 */
export function computeSupplierPurchaseDisplayAmounts(
  hint: SupplierOrderLinkHintDto | undefined,
  quantity: number | null | undefined,
  quantitySource?: ConversionSource,
): SupplierPurchaseDisplayAmounts | null {
  const basis = resolveSupplierPurchaseBasis(hint);
  if (!hint || basis.kind === 'none') {
    return null;
  }

  const purchaseBasisQuantity = resolvePurchaseBasisQuantity(
    hint,
    basis,
    quantity,
    quantitySource,
  );
  if (purchaseBasisQuantity === null) {
    return null;
  }

  const plnAmount = basis.kind === 'wholeOrder'
    ? roundMoney(
      (basis.purchaseValuePLN * purchaseBasisQuantity)
        / basis.purchaseQuantity,
    )
    : roundMoney(basis.unitPurchaseCostPLN * purchaseBasisQuantity);
  if (plnAmount > MAX_MONEY_AMOUNT) {
    return null;
  }

  const sourceBasis = basis.kind === 'wholeOrder'
    ? toFiniteNumber(hint.sourcePurchaseValue)
    : toFiniteNumber(hint.sourceUnitPurchaseCost);
  const sourceAmount = sourceBasis === null
    ? null
    : basis.kind === 'wholeOrder'
      ? roundMoney((sourceBasis * purchaseBasisQuantity) / basis.purchaseQuantity)
      : roundMoney(sourceBasis * purchaseBasisQuantity);

  return {
    plnAmount,
    sourceAmount,
    sourceCurrency: hint.sourceCurrency
      ? String(hint.sourceCurrency).trim().toUpperCase()
      : null,
    purchaseBasisQuantity,
  };
}

/**
 * Compute the editable PLN prefill. Whole-order links scale the saved total by
 * the loaded share; item links multiply the saved PLN unit cost. Results use
 * shared half-up money rounding and never cross currencies silently.
 */
export function computeGoodsValuePrefill(
  hint: SupplierOrderLinkHintDto | undefined,
  quantity: number | null | undefined,
  formCurrency: string | null | undefined,
  quantitySource?: ConversionSource,
): number | null {
  const currency = String(formCurrency || '').trim().toUpperCase();
  if (currency !== SUPPLIER_HINT_CURRENCY) {
    return null;
  }
  return computeSupplierPurchaseDisplayAmounts(hint, quantity, quantitySource)?.plnAmount ?? null;
}

// True when a goods-value form field counts as "empty" for prefill purposes:
// null/undefined/'' or a numeric 0. A user-entered positive value is never
// overwritten (that guard, plus the per-line touched flag, protects operator
// input).
export function goodsValueIsPrefillable(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return true;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric === 0;
}

export type GoodsValuePrefillAction =
  | { type: 'recompute'; value: number }
  | { type: 'reset' }
  | { type: 'skip' };

/**
 * Single home for the per-cargo-line supplier-hint prefill decision (used by
 * SharedTransportForm's prefill effect). While a line is untouched, the
 * prefill OWNS its goods value and must recompute on EVERY quantity change —
 * not just the first fill. `managedKeys` is the caller's mutable Set of keys
 * this prefill has written a value for in the current session (a ref in the
 * component, a plain Set in tests); this function both reads and updates it,
 * so a line the prefill previously wrote to keeps recomputing, while a
 * non-zero value the prefill never wrote (e.g. seeded from a legacy
 * `existingCargoItem`) is left untouched.
 *
 * Decision table:
 *  - `touched` (operator has edited this line): release the key from
 *    `managedKeys` (in case it was previously prefill-managed) and skip —
 *    the prefill stops managing the line entirely, matching the rulebook's
 *    "the moment the operator manually edits, stop managing".
 *  - not touched, current value is prefillable-empty (0/null/undefined/'') OR
 *    the key is already in `managedKeys`: eligible to (re)prefill. Compute
 *    the applicable purchase-value basis via `computeGoodsValuePrefill`; if a
 *    concrete value comes back, claim the key in `managedKeys` and return a
 *    `recompute` action (even when the recomputed value happens to equal the
 *    current one — callers only write on `recompute`, so a match is a no-op
 *    write, not a behavior difference).
 *  - not touched, no hint value available (ambiguous/absent, or non-positive
 *    quantity): release the key from `managedKeys` (nothing left to recompute
 *    FROM) and skip, leaving any existing value alone.
 *  - not touched, prefill-MANAGED, and the ONLY reason prefill failed is a
 *    currency mismatch (concrete PLN hint, form currency ≠ PLN): release the
 *    managed-key claim and reset the form value. The UI independently derives
 *    and displays the PLN source value as reference, while allocation/payload
 *    remain empty until the operator enters the transport-currency amount.
 *  - not touched, current value is a non-empty value the prefill never wrote
 *    (not prefillable-empty AND not in `managedKeys`): skip — this is
 *    operator/legacy data outside the prefill's ownership, never clobbered.
 */
export function resolveGoodsValuePrefillAction(
  params: {
    touched: boolean;
    currentGoodsValueTotal: unknown;
    quantity: number | null | undefined;
    quantitySource?: ConversionSource;
    hint: SupplierOrderLinkHintDto | undefined;
    currency: string | null | undefined;
    key: string;
    managedKeys: Set<string>;
  },
): GoodsValuePrefillAction {
  const {
    touched,
    currentGoodsValueTotal,
    quantity,
    quantitySource,
    hint,
    currency,
    key,
    managedKeys,
  } = params;

  if (touched) {
    managedKeys.delete(key);
    return { type: 'skip' };
  }

  const eligibleToPrefill = goodsValueIsPrefillable(currentGoodsValueTotal) || managedKeys.has(key);
  if (!eligibleToPrefill) {
    return { type: 'skip' };
  }

  const prefill = computeGoodsValuePrefill(hint, quantity, currency, quantitySource);
  if (prefill === null) {
    const wasManaged = managedKeys.has(key);
    managedKeys.delete(key);
    const provenance = resolveCargoHintProvenance(hint, currency);
    if (wasManaged && provenance.kind === 'currencyMismatch') {
      return { type: 'reset' };
    }
    return { type: 'skip' };
  }

  managedKeys.add(key);
  return { type: 'recompute', value: prefill };
}

// 'WEIGHT' ("Waga") is retired as an operator-facing between-orders option
// (rulebook decision #6) but stays in this type so a legacy transport already
// saved with it still type-checks and renders/edits correctly — the picker
// itself only offers it when it is already a transport's stored value (see
// betweenOrdersMethods in TransportCostsSection.tsx / addOrdersAllocationMethodOptions
// in TransportRouteDetail.tsx). Do not add it back as a selectable default.
export const ALLOCATION_METHODS = ['LDM', 'CBM', 'SQM', 'QUANTITY', 'WEIGHT'] as const;
export type AllocationMethod = typeof ALLOCATION_METHODS[number];

// F5 (soft mirroring, small): when the operator changes the between-orders
// split key, suggest the SAME family for the item-level split — LDM/CBM/SQM
// map 1:1, QUANTITY maps to the item-level "commercial quantity" equivalent.
// WEIGHT (a legacy-only between-orders value, see betweenOrdersMethods in
// TransportCostsSection) has no item-level WEIGHT method, so it intentionally
// suggests nothing (caller must treat undefined as "don't touch the field").
// This is a SUGGESTION only — the caller (SharedTransportForm) must never
// apply it once the operator has touched the item-level field themselves this
// session (same touched-guard precedent as resolveGoodsValuePrefillAction).
export function suggestItemAllocationMethodForBetweenOrders(
  betweenOrdersMethod: AllocationMethod,
): TransportCostAllocationMethod | undefined {
  switch (betweenOrdersMethod) {
    case 'LDM':
      return 'BY_LDM';
    case 'CBM':
      return 'BY_CBM';
    case 'SQM':
      return 'BY_SQM';
    case 'QUANTITY':
      return 'BY_COMMERCIAL_QTY';
    case 'WEIGHT':
    default:
      return undefined;
  }
}

export interface SharedTransportOrderMetrics {
  packages: number;
  weight: number;
  areaSqm: number;
  volume: number;
  rawLdm: number;
  ldm: number;
  // Package-based LDM: true when ≥1 loaded line lacked packaging data, so `ldm`
  // is a partial sum and the UI should render "—" or a value + warning.
  ldmIncomplete: boolean;
  quantityByUnit: Record<string, number>;
}

export interface SharedTransportPreviewRow {
  orderId: string;
  displayOrderNumber: string;
  customerName: string;
  basisValue: number;
  allocationPercent: number;
  allocatedTransportCost: number;
  metrics: SharedTransportOrderMetrics;
}

export interface SharedTransportCargoItem {
  id?: string;
  orderId: string;
  orderProductId?: string;
  productName: string;
  quantity: number;
  unit?: string;
  goodsValueTotal?: number | null;
  manualAllocatedTransportCost?: number | null;
  grossWeight?: number;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  // Package-based LDM (logistics) snapshot/override per cargo line.
  piecesPerPackage?: number | null;
  packageLength?: number | null;
  packageWidth?: number | null;
  packageStackLayers?: number | null;
  // F2: operator-entered LDM override for this line; wins over packaging when
  // positive.
  manualLdm?: number | null;
}

export type SharedTransportFormValues = TransportFormValues & {
  allocationMethod?: AllocationMethod;
  costAllocationMethod?: TransportCostAllocationMethod;
  [key: string]: any;
};

export function buildSharedTransportFormValues(
  transport?: TransportRouteDetailDto | null,
): SharedTransportFormValues {
  if (!transport) {
    return {
      // DB default is PLN; the create form mirrors that so a saved-as-is
      // transport keeps a consistent currency. Edit mode keeps the stored value.
      currency: DEFAULT_TRANSPORT_CURRENCY,
      status: 'PENDING',
      customer: '',
      transportCompany: '',
      transportCompanyId: null,
      loadingAddresses: [''],
      deliveryAddresses: [''],
      loadingDate: null,
      estimatedDelivery: null,
      actualDelivery: null,
      transportRate: null,
      totalWeight: null,
      totalVolume: null,
      packages: null,
      allocationMethod: 'LDM',
      costAllocationMethod: 'BY_COMMERCIAL_QTY',
    };
  }

  const firstCustomerName = transport.routeOrders[0]?.order?.customerName || transport.customerName || '';

  return {
    ...buildTransportFormValues(transport, {
      customer: { name: firstCustomerName },
      currency: transport.currency,
      factoryLocation: transport.loadingAddresses?.[0] || '',
      deliveryLocation: transport.deliveryAddresses?.[0] || '',
    }),
    customer: firstCustomerName,
    transportCompanyId: transport.transportCompanyId ?? null,
    allocationMethod: (transport.routeOrders[0]?.allocationMethod as AllocationMethod | undefined) || 'LDM',
    costAllocationMethod: transport.costAllocationMethod || 'BY_COMMERCIAL_QTY',
  };
}

export function buildSharedTransportPayload(
  values: SharedTransportFormValues,
  orderIds: string[],
  cargoItems: SharedTransportCargoItem[] = [],
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    ...buildTransportRequestPayload(values),
    orderIds,
    allocationMethod: values.allocationMethod || 'LDM',
    costAllocationMethod: values.costAllocationMethod || 'BY_COMMERCIAL_QTY',
    cargoItems,
  };

  delete payload.cargoItemsByKey;
  return payload;
}

function sortedIds(values: unknown): string[] {
  return Array.isArray(values)
    ? values.map((value) => String(value || '')).filter(Boolean).sort()
    : [];
}

function normalizeCargoItemsForCompare(values: unknown): Array<Record<string, string | number | null>> {
  if (!Array.isArray(values)) {
    return [];
  }

  return values.map((item) => ({
    orderId: item.orderId || null,
    orderProductId: item.orderProductId || null,
    productName: item.productName || '',
    quantity: toFiniteNumber(item.quantity) || 0,
    unit: normalizeProductUnit(item.unit),
    goodsValueTotal: toFiniteNumber(item.goodsValueTotal) || 0,
    manualAllocatedTransportCost: toFiniteNumber(item.manualAllocatedTransportCost) || 0,
    grossWeight: toFiniteNumber(item.grossWeight) || 0,
    width: toFiniteNumber(item.width) || 0,
    height: toFiniteNumber(item.height) || 0,
    length: toFiniteNumber(item.length) || 0,
    thickness: toFiniteNumber(item.thickness) || 0,
    // Include packaging so a packaging-only edit dirties the form and is saved.
    piecesPerPackage: toFiniteNumber(item.piecesPerPackage) || 0,
    packageLength: toFiniteNumber(item.packageLength) || 0,
    packageWidth: toFiniteNumber(item.packageWidth) || 0,
    packageStackLayers: toFiniteNumber(item.packageStackLayers) || 0,
    // F2: include the manual LDM override so a manual-LDM-only edit also
    // dirties the form and is saved, same reasoning as packaging above.
    manualLdm: toFiniteNumber(item.manualLdm) || 0,
  })).sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)));
}

export function getEffectiveTransportCargoItems(transport: TransportRouteDetailDto | null | undefined): SharedTransportCargoItem[] {
  if (!transport) {
    return [];
  }

  if (transport.cargoItems.length > 0) {
    return transport.cargoItems;
  }

  return transport.routeOrders.flatMap((routeOrder) => (
    (routeOrder.order.products || []).map((product) => ({
      orderId: routeOrder.orderId,
      orderProductId: product.id,
      productName: product.name,
      quantity: product.quantity ?? 0,
      unit: product.unit,
      goodsValueTotal: 0,
      manualAllocatedTransportCost: null,
      grossWeight: product.grossWeight,
      width: product.width,
      height: product.height,
      length: product.length,
      thickness: product.thickness,
      // Product packaging defaults feed the synthesized-cargo LDM display.
      piecesPerPackage: product.piecesPerPackage ?? null,
      packageLength: product.packageLength ?? null,
      packageWidth: product.packageWidth ?? null,
      packageStackLayers: product.packageStackLayers ?? null,
      // F2: manualLdm is a per-cargo-line override (TransportCargoItem only,
      // never a Product default) — nothing to synthesize for a legacy
      // transport with no persisted cargo rows at all.
      manualLdm: null,
    }))
  ));
}

// HOTEL (HIGH, "warn, never block"): a cargo line's PERSISTED loaded quantity
// can end up exceeding the order product's CURRENT quantity when the order is
// edited down after the transport was already built (e.g. order 120 -> 100
// szt, transport still shows/loads 120 with zero indication). This is a
// non-blocking, informational detection ONLY — no auto-resync, no save
// blocking (the existing per-line overflow guard in
// TransportOrdersCargoSection, which compares against allocatedElsewhere
// across ALL transports, stays exactly as-is and is a separate concern).
//
// Single home so TransportRouteDetail.tsx (read-only) and the edit form
// (SharedTransportForm/TransportOrdersCargoSection) render the identical
// warning from the identical detection, rather than reimplementing the
// order-product lookup twice. Both surfaces already have everything this
// needs in their existing DTOs — routeOrders[].order.products[].quantity is a
// LIVE `SELECT` against "OrderProduct" (transportRepository.js), never a
// cargo snapshot, so no backend/DTO change was required.
export interface StaleCargoQuantityWarning {
  orderId: string;
  orderProductId: string;
  displayOrderNumber: string;
  productName: string;
  loadedQuantity: number;
  currentQuantity: number;
  unit?: string;
}

export function findStaleCargoQuantityWarnings(
  transport: Pick<TransportRouteDetailDto, 'cargoItems' | 'routeOrders'> | null | undefined,
): StaleCargoQuantityWarning[] {
  if (!transport) {
    return [];
  }

  // Map of orderProductId -> { current OrderProduct quantity, owning order's
  // display number }. Built once across every linked order so a cargo item is
  // matched regardless of which order it belongs to.
  const currentProductsById = new Map<string, { quantity: number; displayOrderNumber: string; orderId: string }>();
  // S25 (CRITICAL, defense in depth): orderId -> productName -> orderProductId,
  // for the same NULL-orderProductId fallback the backend allocation guard
  // uses (getTransportsForOrder/transportService.js) — an orphaned cargo row
  // (ON DELETE SET NULL) must still be checked against the CURRENT quantity
  // instead of going blind, which is exactly the scenario this banner exists
  // to catch.
  const productIdByOrderAndName = new Map<string, string>();
  (transport.routeOrders || []).forEach((routeOrder: RouteOrderDto) => {
    const displayOrderNumber = routeOrder.order?.displayOrderNumber || routeOrder.order?.orderNumber || '';
    (routeOrder.order?.products || []).forEach((product) => {
      if (!product.id) {
        return;
      }
      currentProductsById.set(product.id, {
        quantity: toFiniteNumber(product.quantity) ?? 0,
        displayOrderNumber,
        orderId: routeOrder.orderId,
      });
      const nameKey = `${routeOrder.orderId}::${product.name || ''}`;
      if (product.name && !productIdByOrderAndName.has(nameKey)) {
        productIdByOrderAndName.set(nameKey, product.id);
      }
    });
  });

  const warnings: StaleCargoQuantityWarning[] = [];
  (transport.cargoItems || []).forEach((cargoItem) => {
    const fallbackOrderId = cargoItem.orderId;
    const fallbackNameKey = fallbackOrderId ? `${fallbackOrderId}::${cargoItem.productName || ''}` : null;
    const resolvedOrderProductId = cargoItem.orderProductId
      || (fallbackNameKey ? productIdByOrderAndName.get(fallbackNameKey) : undefined);

    if (!resolvedOrderProductId) {
      // No FK to compare against and no name-based fallback match (legacy row,
      // or the line was fully removed from the order, not just reduced) —
      // nothing reliable to detect.
      return;
    }

    const currentProduct = currentProductsById.get(resolvedOrderProductId);
    if (!currentProduct) {
      // The order product this cargo line pointed to no longer exists on the
      // order at all (fully removed, not just reduced) — a different failure
      // mode than HOTEL's "quantity reduced" repro; not surfaced here.
      return;
    }

    const loadedQuantity = toFiniteNumber(cargoItem.quantity) ?? 0;
    if (loadedQuantity > currentProduct.quantity) {
      warnings.push({
        orderId: currentProduct.orderId,
        orderProductId: resolvedOrderProductId,
        displayOrderNumber: currentProduct.displayOrderNumber,
        productName: cargoItem.productName,
        loadedQuantity,
        currentQuantity: currentProduct.quantity,
        unit: cargoItem.unit,
      });
    }
  });

  return warnings;
}

// F3 (order coverage on transport surfaces, small): "Załadowano X z Y szt." —
// how much of an order's total quantity is loaded on THIS transport, vs the
// order's current total. Reuses the SAME data findStaleCargoQuantityWarnings
// uses (routeOrders[].order.products for the current order quantity,
// transport.cargoItems for what is loaded here) — synergy intended, no
// separate lookup. Summed per unit (mirrors formatCargoQuantitySummary /
// formatCargoQuantityTotals) since an order can carry mixed units (pcs/m²/m³).
export interface OrderCoverageByUnit {
  unit: string;
  loadedQuantity: number;
  orderedQuantity: number;
}

export function deriveOrderCoverage(
  transport: Pick<TransportRouteDetailDto, 'cargoItems' | 'routeOrders'> | null | undefined,
  orderId: string,
): OrderCoverageByUnit[] {
  if (!transport) {
    return [];
  }

  const routeOrder = (transport.routeOrders || []).find((entry: RouteOrderDto) => entry.orderId === orderId);
  if (!routeOrder) {
    return [];
  }

  const orderedByUnit = new Map<string, number>();
  (routeOrder.order?.products || []).forEach((product) => {
    const unit = product.unit || 'pcs';
    const quantity = toFiniteNumber(product.quantity) ?? 0;
    orderedByUnit.set(unit, (orderedByUnit.get(unit) || 0) + quantity);
  });

  const loadedByUnit = new Map<string, number>();
  getEffectiveTransportCargoItems(transport as TransportRouteDetailDto)
    .filter((item) => item.orderId === orderId)
    .forEach((item) => {
      const unit = item.unit || 'pcs';
      const quantity = toFiniteNumber(item.quantity) ?? 0;
      loadedByUnit.set(unit, (loadedByUnit.get(unit) || 0) + quantity);
    });

  const units = new Set([...Array.from(orderedByUnit.keys()), ...Array.from(loadedByUnit.keys())]);
  return Array.from(units).map((unit) => ({
    unit,
    loadedQuantity: loadedByUnit.get(unit) || 0,
    orderedQuantity: orderedByUnit.get(unit) || 0,
  }));
}

// Single home for the per-line description text of a stale-quantity warning,
// so TransportRouteDetail.tsx and the edit form's Alert render byte-identical
// wording — the multi-order variant prefixes the display order number
// (`transport:cargo.staleQuantityWarningItemMultiOrder`) only when more than
// one distinct order is affected, matching the "use display order number when
// multiple orders" requirement without cluttering a single-order transport's
// warning with a redundant order number already shown elsewhere on the page.
// Both keys live under the `cargo` namespace object in transport.json (both
// locales) — S31 MED regression (2026-07-07 external run): the key strings
// here were missing that `cargo.` segment, so the banner rendered the raw
// i18n key instead of Polish text. See the JSON-path-agreement test below.
export function formatStaleCargoQuantityWarningItems(
  warnings: StaleCargoQuantityWarning[],
  t: (key: string, options?: Record<string, unknown>) => string,
): string[] {
  const distinctOrderIds = new Set(warnings.map((warning) => warning.orderId));
  const isMultiOrder = distinctOrderIds.size > 1;

  return warnings.map((warning) => t(
    isMultiOrder
      ? 'transport:cargo.staleQuantityWarningItemMultiOrder'
      : 'transport:cargo.staleQuantityWarningItemSingleOrder',
    {
      orderNumber: warning.displayOrderNumber,
      product: warning.productName,
      loaded: warning.loadedQuantity,
      current: warning.currentQuantity,
      unit: formatUnit(warning.unit),
    },
  ));
}

export function omitUnchangedSharedTransportAllocationFields(
  payload: Record<string, unknown>,
  transport: TransportRouteDetailDto,
): Record<string, unknown> {
  const nextPayload = { ...payload };
  const currentOrderIds = sortedIds(transport.routeOrders.map((routeOrder) => routeOrder.orderId));
  const requestedOrderIds = sortedIds(nextPayload.orderIds);
  const currentAllocationMethod = transport.routeOrders[0]?.allocationMethod || 'LDM';
  const currentCostAllocationMethod = transport.costAllocationMethod || 'BY_COMMERCIAL_QTY';

  if (JSON.stringify(currentOrderIds) === JSON.stringify(requestedOrderIds)) {
    delete nextPayload.orderIds;
  }

  if (nextPayload.allocationMethod === currentAllocationMethod) {
    delete nextPayload.allocationMethod;
  }

  if (nextPayload.costAllocationMethod === currentCostAllocationMethod) {
    delete nextPayload.costAllocationMethod;
  }

  if (
    JSON.stringify(normalizeCargoItemsForCompare(nextPayload.cargoItems))
    === JSON.stringify(normalizeCargoItemsForCompare(getEffectiveTransportCargoItems(transport)))
  ) {
    delete nextPayload.cargoItems;
  }

  return nextPayload;
}

export function getCargoQuantityUnits(cargoItems: SharedTransportCargoItem[]): string[] {
  const units = new Set<string>();

  cargoItems.forEach((item) => {
    const quantity = toFiniteNumber(item.quantity) || 0;
    if (quantity > 0) {
      units.add(normalizeProductUnit(item.unit));
    }
  });

  return Array.from(units).sort();
}

export function hasMixedCargoQuantityUnits(cargoItems: SharedTransportCargoItem[]): boolean {
  return getCargoQuantityUnits(cargoItems).length > 1;
}

export function calculateOrderMetrics(order: Pick<OrderOptionDto, 'products'> | Pick<RouteOrderDto, 'order'>): SharedTransportOrderMetrics {
  const products = 'products' in order ? order.products : order.order.products;
  return calculateProductsMetrics(products || []);
}

export function calculateCargoItemsMetrics(cargoItems: SharedTransportCargoItem[]): SharedTransportOrderMetrics {
  return calculateProductsMetrics(cargoItems);
}

// S14 (HIGH): full-precision counterparts of the two functions above, for
// buildAllocationPreview's basis derivation — see calculateProductsMetricsRaw's
// comment: the live preview must use the SAME unrounded measure the backend
// persists from, or the preview total drifts a few cents from what actually
// gets saved.
function calculateOrderMetricsRaw(order: Pick<OrderOptionDto, 'products'> | Pick<RouteOrderDto, 'order'>): SharedTransportOrderMetrics {
  const products = 'products' in order ? order.products : order.order.products;
  return calculateProductsMetricsRaw(products || []);
}

export function calculateCargoItemsMetricsRaw(cargoItems: SharedTransportCargoItem[]): SharedTransportOrderMetrics {
  return calculateProductsMetricsRaw(cargoItems);
}

// F1 (trailer capacity indicator, medium): display-only utilization vs a
// standard trailer (TRAILER_CAPACITY_LDM / TRAILER_PAYLOAD_KG, single home in
// transportCostAllocation.ts). NEVER a save-blocking limit — real trailers and
// special permits vary, so this is purely informational (badge/progress).
export interface TrailerCapacityIndicator {
  ratio: number; // 0..Infinity, e.g. 0.55 = 55% of capacity used
  percent: number; // ratio * 100, rounded to 1dp for display
  overCapacity: boolean; // ratio > 1
  loaded: number;
  capacity: number;
}

// An incomplete LDM total (≥1 loaded line missing packaging data — see
// SharedTransportOrderMetrics.ldmIncomplete) is a PARTIAL sum, not the real
// total, so a capacity ratio computed from it would understate utilization.
// Callers should treat `null` as "don't render" rather than falling back to a
// misleading 0%/partial badge.
export function deriveLdmCapacityIndicator(totalLdm: number, ldmIncomplete: boolean): TrailerCapacityIndicator | null {
  if (ldmIncomplete) {
    return null;
  }
  const ratio = totalLdm / TRAILER_CAPACITY_LDM;
  return {
    ratio,
    percent: Number((ratio * 100).toFixed(1)),
    overCapacity: ratio > 1,
    loaded: totalLdm,
    capacity: TRAILER_CAPACITY_LDM,
  };
}

// Weight indicator is intentionally ONLY shown when totalWeight > 0 — most
// cargo lines have no grossWeight entered today, so a 0.00 kg weight would
// otherwise render a permanently-empty, noisy "0 / 24000 kg" badge on every
// transport (false-alarm-free by construction, per the F1 spec).
export function deriveWeightCapacityIndicator(totalWeight: number): TrailerCapacityIndicator | null {
  if (!(totalWeight > 0)) {
    return null;
  }
  const ratio = totalWeight / TRAILER_PAYLOAD_KG;
  return {
    ratio,
    percent: Number((ratio * 100).toFixed(1)),
    overCapacity: ratio > 1,
    loaded: totalWeight,
    capacity: TRAILER_PAYLOAD_KG,
  };
}

// Read-only per-line/per-truck metrics for a single dimensioned source at a
// given piece quantity. Area and volume come straight from the shared
// dimensionConversion helpers (the single home for the piece → m²/m³ formula,
// mirrored from the backend) so the transport form never re-derives geometry.
// LDM follows the same "area ÷ trailer width" rule the aggregate stats row uses.
// `areaSqm`/`volume` are `null` when the source lacks the dimensions needed to
// compute them, so the UI can render a "—" fill-in-dimensions state.
export interface CargoLineMetrics {
  areaSqm: number | null;
  volume: number | null;
  ldm: number | null;
  // F2 (manual LDM override): true when `ldm` came from a positive manualLdm
  // rather than the packaging calculation — drives the "ręcznie" tag/tooltip.
  ldmIsManual: boolean;
}

export function calculateCargoLineMetrics(
  source: ConversionSource & {
    unit?: string | null;
    piecesPerPackage?: number | null;
    packageLength?: number | null;
    packageWidth?: number | null;
    packageStackLayers?: number | null;
    manualLdm?: number | null;
  },
  quantity: number,
): CargoLineMetrics {
  const areaSqm = calculateAreaM2(source, quantity);
  const volume = calculateVolumeM3(source, quantity);
  // Package-based LDM (docs/decisions/2026-07-06-ldm-package-based.md), or a
  // manual override (F2) when present: the LDM shown per line is the
  // footprint its packages occupy on the trailer bed (or the operator-entered
  // value), matching the basis the allocation splits by. Null when neither is
  // available, so the cell renders "—".
  const { ldm, isManual: ldmIsManual } = resolveItemLdm({
    quantity,
    unit: source.unit,
    width: source.width,
    length: source.length,
    height: source.height,
    thickness: source.thickness,
    piecesPerPackage: source.piecesPerPackage,
    packageLengthMm: source.packageLength,
    packageWidthMm: source.packageWidth,
    packageStackLayers: source.packageStackLayers,
    manualLdm: source.manualLdm,
  });

  return {
    areaSqm: areaSqm !== null ? Number(areaSqm.toFixed(3)) : null,
    volume: volume !== null ? Number(volume.toFixed(3)) : null,
    ldmIsManual,
    ldm: ldm !== null ? Number(ldm.toFixed(3)) : null,
  };
}

// True when the line carries a positive quantity but no metric can be derived
// (missing dimensions) — the "—" / tooltip signal for a single cargo line.
export function cargoLineMetricsAreEmpty(metrics: CargoLineMetrics): boolean {
  return metrics.areaSqm === null && metrics.volume === null && metrics.ldm === null;
}

// True when any loaded item (quantity > 0) lacks the dimension fields needed to
// derive a volume, so the cargo totals understate reality. Volume is the right
// signal: pcs without length/width/height and m² without thickness both yield
// zero, while m³ bulk cargo carries its own volume and is never flagged. The
// geometry knowledge stays in calculateProductVolumeCbm rather than being
// re-derived in the form component.
export function hasMissingCargoDimensions(cargoItems: SharedTransportCargoItem[]): boolean {
  return cargoItems.some((item) => {
    const quantity = toFiniteNumber(item.quantity) || 0;
    if (quantity <= 0) {
      return false;
    }
    return calculateProductVolumeCbm(item) <= 0;
  });
}

type ProductsMetricsInput = Array<{
  quantity?: number;
  unit?: string;
  grossWeight?: number;
  length?: number;
  width?: number;
  height?: number;
  thickness?: number;
  piecesPerPackage?: number | null;
  packageLength?: number | null;
  packageWidth?: number | null;
  packageStackLayers?: number | null;
  manualLdm?: number | null;
}>;

// S14 (HIGH): full-precision totals, mirroring the backend twin's
// calculateTransportMetrics (transportMetrics.js) — UNROUNDED. Allocation
// math (buildAllocationPreview/deriveBasisValue) must consume THIS, never the
// 3dp-rounded calculateProductsMetrics below, or a basis value like
// 3.09375 gets truncated to 3.094 before the split is computed, shifting
// real money by a few cents. Display surfaces keep using
// calculateProductsMetrics (rounded, for showing a clean number to the
// operator) — rounding for display is fine; rounding before allocation math
// is the bug.
function computeProductsMetricsTotals(products: ProductsMetricsInput) {
  return (products || []).reduce(
    (accumulator, product) => {
      const quantity = toFiniteNumber(product.quantity) || 0;
      const unit = normalizeProductUnit(product.unit);
      const grossWeight = toFiniteNumber(product.grossWeight) || 0;
      const areaSqm = calculateProductAreaSqm(product);
      const volume = calculateProductVolumeCbm(product);
      // Package-based LDM (docs/decisions/2026-07-06-ldm-package-based.md), or
      // a manual override (F2) when present: the footprint the packages/
      // pallets occupy on the trailer bed, or the operator-entered value. Null
      // when neither is available on a loaded line → contributes 0 and flags
      // ldmIncomplete so the UI shows "—" or a partial value + warning. Routes
      // through the SAME helper (resolveItemLdm) the allocation splits by, so
      // the display LDM and the split LDM are equal by construction.
      const lineLdm = resolveItemLdm({
        quantity,
        unit: product.unit,
        width: product.width,
        length: product.length,
        height: product.height,
        thickness: product.thickness,
        piecesPerPackage: product.piecesPerPackage,
        packageLengthMm: product.packageLength,
        packageWidthMm: product.packageWidth,
        packageStackLayers: product.packageStackLayers,
        manualLdm: product.manualLdm,
      }).ldm;

      if (unit === 'pcs') {
        accumulator.packages += quantity;
      }
      accumulator.quantityByUnit[unit] = (accumulator.quantityByUnit[unit] || 0) + quantity;
      accumulator.weight += grossWeight * quantity;
      accumulator.areaSqm += areaSqm;
      accumulator.volume += volume;
      if (lineLdm === null) {
        if (quantity > 0) {
          accumulator.ldmIncomplete = true;
        }
      } else {
        accumulator.ldm += lineLdm;
      }

      return accumulator;
    },
    {
      packages: 0,
      weight: 0,
      areaSqm: 0,
      volume: 0,
      ldm: 0,
      ldmIncomplete: false,
      quantityByUnit: {} as Record<string, number>,
    }
  );
}

// S14 (HIGH): the FULL-PRECISION counterpart of calculateProductsMetrics,
// for allocation math only (see computeProductsMetricsTotals above). Shape
// matches SharedTransportOrderMetrics (rawLdm/ldm both carry the same
// unrounded value, consistent with the rounded function's "kept for shape
// compatibility" contract) so it drops straight into
// pickUniformLdmFallbackMetric/deriveBasisValue without any other change.
function calculateProductsMetricsRaw(products: ProductsMetricsInput): SharedTransportOrderMetrics {
  const totals = computeProductsMetricsTotals(products);

  return {
    packages: totals.packages,
    weight: totals.weight,
    areaSqm: totals.areaSqm,
    volume: totals.volume,
    rawLdm: totals.ldm,
    ldm: totals.ldm,
    ldmIncomplete: totals.ldmIncomplete,
    quantityByUnit: totals.quantityByUnit,
  };
}

function calculateProductsMetrics(products: ProductsMetricsInput): SharedTransportOrderMetrics {
  const totals = computeProductsMetricsTotals(products);

  // rawLdm/ldm are the same package-based value (kept for shape compatibility).
  const exactLdm = Number(totals.ldm.toFixed(3));
  return {
    packages: totals.packages,
    weight: Number(totals.weight.toFixed(2)),
    areaSqm: Number(totals.areaSqm.toFixed(3)),
    volume: Number(totals.volume.toFixed(3)),
    rawLdm: exactLdm,
    ldm: exactLdm,
    ldmIncomplete: totals.ldmIncomplete,
    quantityByUnit: totals.quantityByUnit,
  };
}

function normalizeProductUnit(unit: unknown): string {
  return String(unit || 'pcs').trim().toLowerCase();
}

function getProductHeightMeters(product: {
  height?: number;
  thickness?: number;
}): number | null {
  const heightMeters = toMetersFromMillimeters(product.height);
  if (heightMeters !== null) {
    return heightMeters;
  }

  return toMetersFromMillimeters(product.thickness);
}

function calculateProductAreaSqm(product: {
  quantity?: number;
  unit?: string;
  length?: number;
  width?: number;
  height?: number;
  thickness?: number;
}): number {
  const quantity = toFiniteNumber(product.quantity) || 0;
  const unit = normalizeProductUnit(product.unit);

  if (quantity <= 0) {
    return 0;
  }

  if (unit === 'm2') {
    return quantity;
  }

  if (unit === 'm3') {
    const heightMeters = getProductHeightMeters(product);
    return heightMeters ? quantity / heightMeters : 0;
  }

  const lengthMeters = toMetersFromMillimeters(product.length);
  const widthMeters = toMetersFromMillimeters(product.width);

  return lengthMeters && widthMeters ? lengthMeters * widthMeters * quantity : 0;
}

function calculateProductVolumeCbm(product: {
  quantity?: number;
  unit?: string;
  length?: number;
  width?: number;
  height?: number;
  thickness?: number;
}): number {
  const quantity = toFiniteNumber(product.quantity) || 0;
  const unit = normalizeProductUnit(product.unit);

  if (quantity <= 0) {
    return 0;
  }

  if (unit === 'm3') {
    return quantity;
  }

  const heightMeters = getProductHeightMeters(product);
  if (!heightMeters) {
    return 0;
  }

  if (unit === 'm2') {
    return quantity * heightMeters;
  }

  const lengthMeters = toMetersFromMillimeters(product.length);
  const widthMeters = toMetersFromMillimeters(product.width);

  return lengthMeters && widthMeters ? lengthMeters * widthMeters * heightMeters * quantity : 0;
}

export function calculateAggregateMetrics(orders: OrderOptionDto[]): SharedTransportOrderMetrics {
  return orders.reduce(
    (accumulator, order) => {
      const metrics = calculateOrderMetrics(order);

      accumulator.packages += metrics.packages;
      Object.entries(metrics.quantityByUnit).forEach(([unit, quantity]) => {
        accumulator.quantityByUnit[unit] = (accumulator.quantityByUnit[unit] || 0) + quantity;
      });
      accumulator.weight += metrics.weight;
      accumulator.areaSqm += metrics.areaSqm;
      accumulator.volume += metrics.volume;
      accumulator.ldm += metrics.ldm;
      accumulator.ldmIncomplete = accumulator.ldmIncomplete || metrics.ldmIncomplete;

      return accumulator;
    },
    {
      packages: 0,
      weight: 0,
      areaSqm: 0,
      volume: 0,
      rawLdm: 0,
      ldm: 0,
      ldmIncomplete: false,
      quantityByUnit: {},
    } as SharedTransportOrderMetrics
  );
}

export interface SharedTransportAllocationPreview {
  rows: SharedTransportPreviewRow[];
  // The ACTUAL metric the basisValue column is denominated in, always set
  // when allocationMethod is 'LDM' (used for the "effective metric" column
  // header, e.g. "Wartość bazowa (m³)") — undefined for every other method,
  // where the basis unit already matches the picked method 1:1. `null` means
  // no metric could be resolved at all (every basis is 0).
  effectiveLdmMetric?: LdmFallbackMetricKey | 'ldm' | null;
  // Set (non-undefined) ONLY when allocationMethod is 'LDM' and the resolved
  // basis is NOT real LDM — i.e. the silent-fallback case this whole feature
  // exists to surface (INDIA). `null` = no common metric at all (every basis
  // is 0, equal-share fallback); one of the 4 keys = that metric was used
  // uniformly instead of LDM. Absent (undefined) when every order has real
  // LDM (no fallback happened, nothing to warn about) or when the method
  // isn't LDM at all.
  fallbackMetric?: LdmFallbackMetricKey | null;
}

export function buildAllocationPreview(
  orders: OrderOptionDto[],
  transportRate: number | null | undefined,
  allocationMethod: AllocationMethod = 'LDM',
  cargoItems: SharedTransportCargoItem[] = [],
): SharedTransportAllocationPreview {
  const cargoItemsByOrderId = cargoItems.reduce<Record<string, SharedTransportCargoItem[]>>((accumulator, item) => {
    if (!accumulator[item.orderId]) {
      accumulator[item.orderId] = [];
    }

    accumulator[item.orderId].push(item);
    return accumulator;
  }, {});
  // S14 (HIGH): `metrics` (rounded, 3dp) is DISPLAY ONLY — it feeds row.metrics
  // below for the per-order m²/m³/LDM figures shown in the table. `metricsRaw`
  // (full precision) is what the basis/allocation math below must consume, so
  // a value like 3.09375 splits the transport cost as 3.09375, not the
  // display-rounded 3.094 — otherwise every allocation shifts by a few cents.
  const normalizedOrders = orders.map((order) => {
    const cargoItemsForOrder = cargoItemsByOrderId[order.id];
    return {
      order,
      metrics: cargoItemsForOrder?.length
        ? calculateCargoItemsMetrics(cargoItemsForOrder)
        : calculateOrderMetrics(order),
      metricsRaw: cargoItemsForOrder?.length
        ? calculateCargoItemsMetricsRaw(cargoItemsForOrder)
        : calculateOrderMetricsRaw(order),
    };
  });

  // Decided ONCE for the whole preview, never per-order — mirrors the backend
  // twin's buildAllocationDrafts/pickUniformLdmFallbackMetric so the live
  // preview never disagrees with what gets persisted. Uses the RAW metrics so
  // the "is this uniformly positive" check matches the values basisValue is
  // actually derived from below. See the INVARIANT comment on
  // pickUniformLdmFallbackMetric below.
  const uniformLdmBasisMetric = allocationMethod === 'LDM'
    ? pickUniformLdmFallbackMetric(normalizedOrders.map((entry) => entry.metricsRaw))
    : null;

  const withBasis = normalizedOrders.map(({ order, metrics, metricsRaw }) => ({
    order,
    metrics,
    basisValue: deriveBasisValue(metricsRaw, allocationMethod, uniformLdmBasisMetric),
  }));

  const totalBasis = withBasis.reduce((sum, row) => sum + row.basisValue, 0);
  const totalRate = Number(transportRate || 0);

  const allocationPercents = withBasis.map((row) => (totalBasis > 0
    ? Number(((row.basisValue / totalBasis) * 100).toFixed(4))
    : Number((100 / Math.max(withBasis.length, 1)).toFixed(4))));
  // Cumulative largest-remainder split (shared allocation util) — every share is
  // >= 0 and the shares sum to the rounded rate exactly, replacing the old
  // "remainder on the last row" logic which could show a negative allocation for
  // tiny rates (e.g. 0.05 over 10 equal orders -> nine 0.01 + one -0.04). Uses
  // the same allocationPercent weights that drive the backend persistence path.
  const allocatedCosts = allocateProportionally(totalRate, allocationPercents);

  const rows = withBasis.map((row, index) => ({
    orderId: row.order.id,
    displayOrderNumber: row.order.displayOrderNumber || row.order.orderNumber,
    customerName: row.order.customerName,
    // Display-rounded to 3dp for the table cell — the actual split above
    // already happened against the full-precision basisValue.
    basisValue: Number(row.basisValue.toFixed(3)),
    allocationPercent: allocationPercents[index],
    allocatedTransportCost: allocatedCosts[index] || 0,
    metrics: row.metrics,
  }));

  return {
    rows,
    effectiveLdmMetric: allocationMethod === 'LDM' ? uniformLdmBasisMetric : undefined,
    fallbackMetric: allocationMethod === 'LDM' && uniformLdmBasisMetric !== 'ldm'
      ? uniformLdmBasisMetric
      : undefined,
  };
}

// Metric keys (in priority order) used when LDM is not uniformly available
// across every order in the preview. Kept as an ordered list so
// pickUniformLdmFallbackMetric can walk it looking for a key that is strictly
// positive for EVERY order, rather than each order picking its own
// first-available metric independently. Mirrors the backend twin's
// LDM_FALLBACK_METRIC_KEYS (volume/area/packages/weight, same order).
const LDM_FALLBACK_METRIC_KEYS = ['volume', 'areaSqm', 'packages', 'weight'] as const;
export type LdmFallbackMetricKey = typeof LDM_FALLBACK_METRIC_KEYS[number];

// Single home for the i18n label key (transport:allocation.methods.*) of a
// resolved LDM basis metric — used both for the "effective metric" column
// header and the fallback warning message, so the two surfaces can never show
// different wording for the same decision.
export function ldmBasisMetricLabelKey(metric: LdmFallbackMetricKey | 'ldm'): string {
  switch (metric) {
    case 'volume':
      return 'allocation.methods.cbm';
    case 'areaSqm':
      return 'allocation.methods.sqm';
    case 'packages':
      return 'allocation.methods.packages';
    case 'weight':
      return 'allocation.methods.weight';
    case 'ldm':
    default:
      return 'allocation.methods.ldm';
  }
}

// INVARIANT: bases for one preview (and the transport it will persist to) must
// all be in the same unit. Mixing LDM (metres) for one order with m³/m²/
// packages/kg for another and dividing the cost proportionally would split
// money across incompatible units. Mirrors the backend twin's
// pickUniformLdmFallbackMetric exactly so the live preview and the persisted
// split can never disagree. When package-based LDM is incomplete for at least
// one order, the WHOLE preview falls back together to the first metric in
// LDM_FALLBACK_METRIC_KEYS that is strictly positive for every order; if none
// qualifies for all orders, every basis is 0 (existing zero-basis handling —
// equal-share fallback in buildAllocationPreview — applies to the whole set).
function pickUniformLdmFallbackMetric(
  perOrderMetrics: SharedTransportOrderMetrics[],
): LdmFallbackMetricKey | 'ldm' | null {
  const allOrdersHaveLdm = perOrderMetrics.every((metrics) => Number(metrics.ldm) > 0 && !metrics.ldmIncomplete);
  if (allOrdersHaveLdm) {
    return 'ldm';
  }

  const uniformKey = LDM_FALLBACK_METRIC_KEYS.find((key) =>
    perOrderMetrics.every((metrics) => Number(metrics[key]) > 0),
  );

  // null means "no common metric" — every order's basis resolves to 0 below,
  // which buildAllocationPreview already treats as an equal-share split.
  return uniformKey ?? null;
}

// `ldmBasisMetric` distinguishes "not provided" (undefined — derive a basis key
// for this ONE order in isolation) from "the preview-level decision is: no
// common metric" (null — every order's LDM basis resolves to 0). Do NOT
// collapse this to `ldmBasisMetric || fallback()`: null is a valid, meaningful
// decision and `||` would silently discard it, reintroducing the exact
// mixed-unit bug this guards. Mirrors the backend twin's
// deriveAllocationBasisValue — including the fact that a legacy 'WEIGHT'
// value (no longer an operator-facing option, see ALLOCATION_METHODS' own
// comment) is NOT special-cased here either; it falls through to the same
// LDM-with-fallback branch the backend twin now uses for any unrecognized
// method, so a legacy WEIGHT transport's live preview can never disagree with
// what the backend would actually persist.
function deriveBasisValue(
  metrics: SharedTransportOrderMetrics,
  allocationMethod: AllocationMethod,
  ldmBasisMetric: LdmFallbackMetricKey | 'ldm' | null | undefined = undefined,
): number {
  switch (allocationMethod) {
    case 'CBM':
      return metrics.volume;
    case 'SQM':
      return metrics.areaSqm;
    case 'QUANTITY':
      return deriveQuantityBasisValue(metrics);
    case 'LDM':
    default: {
      const basisMetric = ldmBasisMetric !== undefined
        ? ldmBasisMetric
        : pickUniformLdmFallbackMetric([metrics]);
      return basisMetric ? (Number(metrics[basisMetric]) || 0) : 0;
    }
  }
}

function deriveQuantityBasisValue(metrics: SharedTransportOrderMetrics): number {
  const entries = Object.entries(metrics.quantityByUnit)
    .filter(([, quantity]) => Number.isFinite(quantity) && quantity > 0);

  return entries.length === 1 ? entries[0][1] : 0;
}

function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function toMetersFromMillimeters(value: unknown): number | null {
  const numericValue = toFiniteNumber(value);

  if (numericValue === null || numericValue <= 0) {
    return null;
  }

  return numericValue / 1000;
}

// HIGH (Enter submit): pressing Enter in a single-line input anywhere in the
// transport form (e.g. "Firma transportowa", the split-count InputNumber, a
// per-truck rate InputNumber) bubbles up to antd's <Form onFinish> and
// CREATES the transport mid-configuration — a very easy accidental submit
// while an operator is still tabbing through fields. This is the single home
// for the guard: a form-level onKeyDown handler (wired in SharedTransportForm's
// <Form onKeyDown={...}>) that calls this on every keydown and prevents the
// default (which is what triggers the implicit submit) UNLESS the event
// originated in a <textarea> (the notes field, where Enter must insert a
// newline) or came from an explicit submit control (a real <button> — so the
// actual "Zapisz" button, or hitting Enter while it is focused, still works).
export function shouldPreventEnterFormSubmit(event: {
  key: string;
  target: EventTarget | null;
}): boolean {
  if (event.key !== 'Enter') {
    return false;
  }

  const target = event.target as HTMLElement | null;
  const tagName = target?.tagName;

  if (tagName === 'TEXTAREA') {
    // Notes field: Enter must insert a newline, not submit.
    return false;
  }

  if (tagName === 'BUTTON') {
    // The explicit submit button (or any other real <button>) keeps working
    // exactly as a user expects when it is focused and Enter/Space-activated.
    return false;
  }

  // Every other element (<input>, antd's AutoComplete/InputNumber inner
  // input, a <select>, etc.) must not implicit-submit on Enter.
  return true;
}
